import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { oracleApi, huddleApi, evolutionApi, genesisApi, executionsApi, terminalApi } from "@/lib/api-client";
import { SovereignRFQCard } from "@/components/uc/SovereignRFQCard";
import { toast } from "sonner";
import { Crown, ArrowLeft, Send, Loader2, Eye, Landmark, Sunrise, ChevronDown, Swords, Zap, PenLine, Infinity as InfinityIcon, Rocket, Copy, X, RotateCcw, Mic, Square } from "lucide-react";

function newSessionId() {
  return `oracle-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function MorningBriefing() {
  // SCRUB THE NOISE — the Oracle opens only on manual command: the briefing is
  // fetched ONLY when the Commander expands it. No auto-generated chatter.
  const [b, setB] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !b) {
      setBusy(true);
      try { setB(await oracleApi.morningBriefing()); }
      catch (e) { toast.error("Briefing unavailable"); setOpen(false); }
      finally { setBusy(false); }
    }
  };

  const signoff = async () => {
    if (!b?.top_signoff) return;
    try {
      const r = await huddleApi.execute(b.top_signoff.huddle_id);
      toast.success(`SIGNED & EXECUTED — ${r.detail}`);
      setB((x) => ({ ...x, top_signoff: { ...x.top_signoff, executed: true } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign-off failed");
    }
  };

  const p = (b && b.produced) || {};
  return (
    <div className="mb-4 border border-amber-500/25 bg-amber-500/[0.03]" data-testid="oracle-morning-briefing">
      <button onClick={toggle} data-testid="morning-briefing-toggle"
              className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-amber-500/[0.05] transition-colors">
        <Sunrise size={13} className="text-amber-400 flex-none"/>
        <span className="font-mono-uc text-[10px] uppercase tracking-widest text-amber-400">
          Morning Briefing{b ? ` · ${b.date}` : " · on command"}
        </span>
        <span className="font-mono-uc text-[9px] text-white/40 ml-2 hidden sm:inline">
          {b ? `${p.briefings || 0} briefings · ${p.drafts || 0} drafts · ${p.huddles || 0} huddles · ${p.intel || 0} intel · ${p.leads || 0} leads overnight`
             : "expand to command the overnight report"}
        </span>
        {busy && <Loader2 size={12} className="animate-spin text-amber-400"/>}
        <ChevronDown size={13} className={`ml-auto text-white/40 transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && b && (
        <div className="px-4 pb-4" data-testid="morning-briefing-body">
          <p className="text-white/75 text-sm leading-relaxed whitespace-pre-wrap">{b.summary}</p>
          {b.top_signoff && (
            <div className="mt-3 border border-amber-500/40 bg-black px-3 py-2.5 flex items-center gap-3 flex-wrap" data-testid="morning-top-signoff">
              <Crown size={12} className="text-amber-400 flex-none"/>
              <div className="min-w-0 flex-1">
                <div className="text-[12px] text-white/90 truncate">{b.top_signoff.title}</div>
                <div className="font-mono-uc text-[9px] text-white/45">
                  Impact ${Math.round(b.top_signoff.impact_usd).toLocaleString()} · {b.top_signoff.dissents} dissent(s)
                </div>
              </div>
              {b.top_signoff.executed ? (
                <span className="font-mono-uc text-[9px] uppercase tracking-widest text-emerald-400">Signed & Executed</span>
              ) : (
                <button data-testid="morning-signoff-btn" onClick={signoff}
                        className="uc-btn-amber px-3 py-1.5 font-mono-uc text-[9px] uppercase tracking-wider flex items-center gap-1.5">
                  <PenLine size={10}/> Approve & Execute
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SignoffCard({ refData }) {
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const fire = async () => {
    setBusy(true);
    try {
      const r = await huddleApi.execute(refData.huddle_id);
      toast.success(`SIGNED & EXECUTED — ${r.detail}`);
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign-off failed (it may already be executed)");
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 border border-amber-500/40 bg-amber-500/[0.05] px-3 py-2 flex items-center gap-3 flex-wrap" data-testid="oracle-signoff-card">
      <Crown size={11} className="text-amber-400 flex-none"/>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] text-white/90 truncate">{refData.title}</div>
        <div className="font-mono-uc text-[8px] text-white/45">
          Impact ${Math.round(refData.impact_usd).toLocaleString()} · {refData.dissents} dissent(s) · pulled from Action Hub
        </div>
      </div>
      {done ? (
        <span className="font-mono-uc text-[8px] uppercase tracking-widest text-emerald-400">Executed</span>
      ) : (
        <button data-testid="oracle-signoff-execute-btn" onClick={fire} disabled={busy}
                className="uc-btn-amber px-2.5 py-1 font-mono-uc text-[8px] uppercase tracking-wider flex items-center gap-1 disabled:opacity-50">
          {busy ? <Loader2 size={9} className="animate-spin"/> : <PenLine size={9}/>} Approve & Execute
        </button>
      )}
    </div>
  );
}

function GenesisModal({ open, onClose, onLaunched }) {
  const [name, setName] = useState("");
  const [mission, setMission] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  if (!open) return null;
  const launch = async () => {
    if (name.trim().length < 2 || mission.trim().length < 10) {
      toast.error("Name (2+ chars) and mission (10+ chars) required"); return;
    }
    setBusy(true);
    toast.info("Genesis Protocol engaged — the Oracle is appointing a lead and forging the Launch Artifact…");
    try {
      const r = await genesisApi.launch(name.trim(), mission.trim());
      setResult(r);
      onLaunched(r);
      toast.success(`'${r.name}' pre-registered — ${r.lead_agent.toUpperCase()} appointed development lead`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Genesis launch failed — retry");
    } finally { setBusy(false); }
  };
  const copyPrompt = async () => {
    try { await navigator.clipboard.writeText(result.genesis_prompt); toast.success("Genesis Prompt copied — paste into the new Neo job"); }
    catch { toast.error("Copy failed — select the text manually"); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="genesis-modal">
      <div className="w-full max-w-2xl border border-amber-500/30 bg-[#0c0c0c] p-6 max-h-[85vh] overflow-y-auto uc-scroll">
        <div className="flex items-center gap-2 mb-4">
          <Rocket size={14} className="text-amber-400" />
          <span className="font-mono-uc text-[11px] uppercase tracking-widest text-amber-400">Genesis Protocol · New Venture</span>
          <button onClick={onClose} data-testid="genesis-close-btn" className="ml-auto text-white/40 hover:text-white"><X size={16} /></button>
        </div>
        {!result ? (
          <>
            <p className="text-white/50 text-xs mb-4">
              Every venture born here ships with the four pillars — Sovereign Bridge hook, SOC2-grade security,
              Boutique design language, Artifact Mandate — and is pre-registered into the Net Worth Pulse before code exists.
            </p>
            <label className="font-mono-uc text-[10px] uppercase tracking-widest text-white/60">Venture Name</label>
            <input data-testid="genesis-name-input" value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="e.g. Obsidian Analytics"
                   className="w-full mt-1 mb-4 bg-black border border-white/15 px-3 py-2.5 text-sm text-white focus:border-amber-500/60 outline-none" />
            <label className="font-mono-uc text-[10px] uppercase tracking-widest text-white/60">Mission</label>
            <textarea data-testid="genesis-mission-input" value={mission} onChange={(e) => setMission(e.target.value)} rows={3}
                      placeholder="What this sub-directorate exists to do, who it serves, how it makes money…"
                      className="w-full mt-1 mb-5 bg-black border border-white/15 px-3 py-2.5 text-sm text-white focus:border-amber-500/60 outline-none resize-none" />
            <button data-testid="genesis-launch-btn" onClick={launch} disabled={busy}
                    className="w-full py-3 bg-amber-500 text-black font-mono-uc text-xs uppercase tracking-widest hover:bg-amber-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Rocket size={13} />}
              {busy ? "Forging Launch Artifact…" : "Launch Genesis"}
            </button>
          </>
        ) : (
          <div data-testid="genesis-result">
            <div className="border border-emerald-500/30 bg-emerald-500/[0.04] p-3 mb-4">
              <div className="font-mono-uc text-[10px] uppercase tracking-widest text-emerald-400 mb-1">
                Pre-registered · app id {result.app_id}
              </div>
              <div className="text-sm text-white/80" data-testid="genesis-lead-line">
                <span className="text-amber-400 font-mono-uc text-xs uppercase">{result.lead_agent}</span> appointed development-phase lead
                {result.assigned_by === "oracle" ? " by the Oracle" : " (heuristic)"} — {result.lead_rationale}
              </div>
              <div className="font-mono-uc text-[10px] text-white/40 mt-1">
                Estate Pulse card is live in STANDBY. Launch Artifact archived to the Executed Vault.
              </div>
              {result.scaffold && (
                <div className="font-mono-uc text-[10px] mt-1.5 flex items-center gap-2 flex-wrap" data-testid="genesis-scaffold-line">
                  <span className={`px-1.5 py-0.5 border ${result.scaffold.status === "GITHUB_REPO_CREATED"
                    ? "border-emerald-500/50 text-emerald-400" : "border-amber-500/40 text-amber-300"}`}>
                    {result.scaffold.status}
                  </span>
                  {result.scaffold.repo && (
                    <a href={result.scaffold.url} target="_blank" rel="noreferrer" data-testid="genesis-repo-link"
                       className="text-emerald-300 hover:underline">{result.scaffold.repo} ↗</a>
                  )}
                  {!result.scaffold.repo && result.scaffold.url && (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${result.scaffold.url}`} target="_blank" rel="noreferrer"
                       data-testid="genesis-bundle-link" className="text-amber-300 hover:underline">Scaffold Bundle ↓</a>
                  )}
                  <span className="text-white/40">bridge hook REGISTERED · {(result.scaffold.files || []).length} file(s)</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 mb-2">
              <span className="font-mono-uc text-[10px] uppercase tracking-widest text-white/60">Genesis Prompt — paste into the new Neo job</span>
              <button data-testid="genesis-copy-btn" onClick={copyPrompt}
                      className="ml-auto uc-chip hover:bg-white/5 flex items-center gap-1.5"><Copy size={10} /> Copy</button>
            </div>
            <pre data-testid="genesis-prompt-text"
                 className="border border-white/10 bg-black p-3 text-[11px] text-white/70 whitespace-pre-wrap max-h-72 overflow-y-auto uc-scroll font-mono">
              {result.genesis_prompt}
            </pre>
            <button data-testid="genesis-done-btn" onClick={onClose}
                    className="mt-4 w-full py-2.5 border border-white/15 font-mono-uc text-xs uppercase tracking-widest text-white/70 hover:border-white/40 transition-colors">
              Close — the bridge awaits its first pulse
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function GenesisLedger({ refreshKey }) {
  const [apps, setApps] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    genesisApi.registry().then((r) => setApps(r.apps || [])).catch(() => {});
  }, [refreshKey]);
  if (!apps.length) return null;
  return (
    <div className="mb-4 border border-white/10 bg-white/[0.02]" data-testid="genesis-ledger">
      <button onClick={() => setOpen((o) => !o)} data-testid="genesis-ledger-toggle"
              className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-white/[0.03] transition-colors">
        <Rocket size={11} className="text-amber-400" />
        <span className="font-mono-uc text-[10px] uppercase tracking-widest text-white/70">
          Genesis Ledger · {apps.length} sub-directorate{apps.length !== 1 ? "s" : ""}
        </span>
        <span className="font-mono-uc text-[9px] uppercase tracking-widest text-white/35 ml-2">
          {apps.filter((a) => a.live).length} live · {apps.filter((a) => !a.live).length} standby
        </span>
        <ChevronDown size={13} className={`ml-auto text-white/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-3 space-y-1.5" data-testid="genesis-ledger-body">
          {apps.map((a) => (
            <div key={a.id} className="flex items-center gap-3 border border-white/5 px-3 py-2 hover:border-white/15 transition-colors flex-wrap"
                 data-testid={`genesis-ledger-row-${a.app_id}`} title={a.lead_rationale}>
              <span className={`uc-dot ${a.live ? "bg-emerald-400 uc-pulsing" : "bg-white/25"}`}></span>
              <span className="text-sm text-white/85">{a.name}</span>
              <span className="font-mono-uc text-[9px] text-white/35">{a.app_id}</span>
              <span className="font-mono-uc text-[8px] uppercase tracking-widest px-1 py-0.5 border border-amber-500/40 text-amber-400"
                    data-testid={`genesis-ledger-lead-${a.app_id}`}>
                {a.lead_agent} leads
              </span>
              <span className="ml-auto font-mono-uc text-[9px] uppercase tracking-widest text-white/40"
                    data-testid={`genesis-ledger-status-${a.app_id}`}>
                {a.live ? "LIVE" : "STANDBY"}
              </span>
              <span className="font-mono-uc text-[9px] text-white/35">
                born {new Date(a.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                {a.last_pulse_at ? ` · pulse ${new Date(a.last_pulse_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : ""}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DirectorsOffice() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState(null);
  const [shadowRunning, setShadowRunning] = useState(false);
  const [evo, setEvo] = useState(null);
  const [evolving, setEvolving] = useState(false);
  const [genesisOpen, setGenesisOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [ledgerKey, setLedgerKey] = useState(0);
  const [blockedCount, setBlockedCount] = useState(0);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recRef = useRef(null);
  const bodyRef = useRef(null);

  // VOCAL PARITY — dictate supreme commands via MediaRecorder + managed Whisper STT
  const toggleMic = async () => {
    if (recording) { recRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        setTranscribing(true);
        try {
          const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
          const r = await terminalApi.transcribe(blob);
          setInput((m) => (m ? m + " " : "") + r.text);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Transcription failed");
        } finally { setTranscribing(false); }
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch { toast.error("Microphone access denied"); }
  };
  const sid = useRef(null);

  const load = useCallback(async () => {
    try {
      let s = localStorage.getItem("uc_oracle_session");
      if (!s) {
        // cross-device continuity: resume the most recent Oracle session
        s = (await oracleApi.latestSession().catch(() => ({}))).session_id || newSessionId();
        localStorage.setItem("uc_oracle_session", s);
      }
      sid.current = s;
      const [h, b] = await Promise.all([
        oracleApi.history(s),
        oracleApi.context().catch(() => null),
      ]);
      setMessages(h);
      setStats(b?.stats || null);
      setEvo(await evolutionApi.status().catch(() => null));
      setBlockedCount(((await executionsApi.list().catch(() => null))?.blocked_mandates || []).length);
    } catch (e) { console.error("oracle load failed", e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    if (!sid.current) {
      sid.current = newSessionId();
      localStorage.setItem("uc_oracle_session", sid.current);
    }
    setInput("");
    setSending(true);
    setMessages((m) => [...m, { id: `local-${Date.now()}`, role: "commander", content: msg }]);
    try {
      const r = await oracleApi.chat(msg, sid.current);
      setMessages((m) => [...m, { id: `local-${Date.now()}-o`, role: "oracle", content: r.reply, meta: r.meta }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The Oracle did not respond — retry");
    } finally { setSending(false); }
  };

  const runShadow = async () => {
    setShadowRunning(true);
    toast.info("Shadow Simulation launched — the Oracle is attacking your own empire…");
    try {
      const r = await oracleApi.shadowSim();
      toast.success(`Survival Report filed to the Vault — ${r.vulnerabilities.length} vulnerability(ies) with counter-measures`);
      setMessages((m) => [...m, {
        id: `local-${Date.now()}-s`, role: "oracle",
        content: `SHADOW SIMULATION COMPLETE — ${r.vulnerabilities.length} exploitable weakness(es):\n\n`
          + r.vulnerabilities.map((v) => `[${(v.severity || "medium").toUpperCase()}] ${v.venture || "portfolio"} — ${v.attack_vector}\nCOUNTER-MEASURE: ${v.counter_measure}`).join("\n\n")
          + `\n\nPosture: ${r.summary}\nFull Survival Report archived in your Private Vault.`,
        meta: { command: "shadow", executed: true },
      }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Shadow Simulation failed — retry");
    } finally { setShadowRunning(false); }
  };

  const runEvolution = async () => {
    setEvolving(true);
    toast.info("Evolution Loop engaged — the Oracle is rewriting all 7 sub-agent prompts…");
    try {
      const r = await evolutionApi.runNow();
      toast.success(`Evolution Cycle ${r.cycle} complete — ${r.adopted.length}/7 prompts optimized; report archived to Vault`);
      setEvo(await evolutionApi.status().catch(() => null));
      setMessages((m) => [...m, {
        id: `local-${Date.now()}-e`, role: "oracle",
        content: `EVOLUTION LOOP — CYCLE ${r.cycle} COMPLETE.\n\nOptimized prompts adopted: ${r.adopted.join(", ") || "none"}.`
          + (r.kept.length ? `\nKept prior prompts (guardrail): ${r.kept.join(", ")}.` : "")
          + `\n\nAll adopted rewrites are LIVE for sweeps, agent runs and huddle reviews. Optimization report archived to the Executed Vault. Optimizer mode: trigger-based (pulse drop / bridge failure / hard-blocks), 48h cooldown.`,
        meta: { command: "evolution", executed: true },
      }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Evolution Loop failed — retry");
    } finally { setEvolving(false); }
  };

  const operatorReset = async () => {
    setResetting(true);
    toast.info("Sovereign Override — Operator Reset engaged, re-fetching every live feed…");
    try {
      const r = await oracleApi.operatorReset();
      const s = r.snapshot || {};
      const estate = s.sovereign_estate_api || {};
      toast.success("Operator Reset complete — cognitive blind spots cleared");
      await load();
      setMessages((m) => [...m, {
        id: `local-${Date.now()}-r`, role: "oracle",
        content: `OPERATOR RESET — SOVEREIGN OVERRIDE EXECUTED.\n\n`
          + `All live feeds re-fetched at ${s.reset_at}:\n`
          + `· Markets: ${s.market?.updated ?? 0} positions synced (${s.market?.crypto_positions ?? 0} crypto · ${s.market?.stock_positions ?? 0} stock)${s.market?.error ? ` — ${s.market.error}` : ""}\n`
          + `· Sovereign Estate API: ${estate.host_live ? "HOST LIVE" : "unreachable"}${estate.endpoint ? ` — metrics via ${estate.endpoint}` : estate.note ? ` — ${estate.note}` : ""}\n`
          + `· Vault artifacts: ${s.vault_artifacts}\n`
          + `· Confirmed revenue (verified ledger): $${(s.confirmed_revenue ?? 0).toLocaleString()}\n`
          + `· Hard-blocked mandates: ${s.hard_blocked_mandates} · Pending briefings: ${s.pending_briefings}\n\n`
          + `Cognitive blind spots cleared. The Oracle now reads from fresh data only.`,
        meta: { command: "operator_reset", executed: true },
      }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operator Reset failed — retry");
    } finally { setResetting(false); }
  };

  const onGenesisLaunched = (r) => {
    setLedgerKey((k) => k + 1);
    setMessages((m) => [...m, {
      id: `local-${Date.now()}-g`, role: "oracle",
      content: `GENESIS PROTOCOL EXECUTED — '${r.name}' is born.\n\n`
        + `Estate app id: ${r.app_id} — pre-registered into the Net Worth Pulse (STANDBY card live on the dashboard; first signed pulse flips it LIVE).\n`
        + `Development-phase lead: ${r.lead_agent.toUpperCase()} — ${r.lead_rationale}\n\n`
        + `The Venture Launch Artifact (Genesis Prompt) carries all four pillars — Sovereign Bridge hook, SOC2-grade security foundation, Boutique design language, Artifact Mandate — plus your Zero-Gap estate identity and Sovereign Tone. It is archived in the Executed Vault and copied from the launch console.\n\n`
        + `NEXT ACTION: open a new Neo job, paste the Genesis Prompt, and add the estate secret to the new app's backend/.env.`,
      meta: { command: "genesis", executed: true },
    }]);
  };

  return (
    <div className="min-h-screen uc-grain relative" data-testid="directors-office">
      <div className="relative z-10 max-w-[1100px] mx-auto p-4 md:p-8 flex flex-col h-screen">
        <header className="mb-4 pb-4 border-b border-white/10 flex items-center gap-4">
          <Link to="/" data-testid="office-back-link"
                className="uc-chip hover:border-amber-500/50 hover:bg-amber-500/10 transition-colors px-3 py-1.5 flex items-center gap-2">
            <ArrowLeft size={11}/> Dashboard
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center border border-amber-500/40" style={{ background: "var(--uc-surface-2)" }}>
              <Crown size={18} className="text-amber-400"/>
            </div>
            <div>
              <div className="font-mono-uc text-[10px] uppercase tracking-[0.3em] text-amber-400/80">DIRECTOR'S OFFICE</div>
              <h1 className="font-serif-display text-2xl leading-none">The Sovereign Oracle</h1>
            </div>
          </div>
          {stats && (
            <div className="ml-auto hidden md:flex items-center gap-2" data-testid="oracle-context-strip">
              <button
                data-testid="new-venture-btn"
                onClick={() => setGenesisOpen(true)}
                title="Genesis Protocol — launch a new sub-directorate with the four mandatory pillars"
                className="uc-chip flex items-center gap-1.5 hover:border-amber-500/60 hover:bg-amber-500/10 transition-colors text-amber-400"
              >
                <Rocket size={10} />
                New Venture
              </button>
              <button
                data-testid="operator-reset-btn"
                onClick={operatorReset}
                disabled={resetting}
                title="Sovereign Override — One-Click Operator Reset: force re-fetch of all live API data to clear cognitive blind spots"
                className="uc-chip flex items-center gap-1.5 hover:border-rose-500/60 hover:bg-rose-500/10 transition-colors text-rose-300 disabled:opacity-50"
              >
                {resetting ? <Loader2 size={10} className="animate-spin"/> : <RotateCcw size={10}/>}
                Operator Reset
              </button>
              <button
                data-testid="shadow-sim-btn"
                onClick={runShadow}
                disabled={shadowRunning}
                className="uc-chip flex items-center gap-1.5 hover:border-red-500/50 hover:bg-red-500/10 transition-colors text-red-400 disabled:opacity-50"
              >
                {shadowRunning ? <Loader2 size={10} className="animate-spin"/> : <Swords size={10}/>}
                Shadow Simulation
              </button>
              <button
                data-testid="evolution-loop-btn"
                onClick={runEvolution}
                disabled={evolving}
                title="Recursive Mandate — Oracle rewrites all 7 sub-agent prompts from live performance data (auto every 30 days)"
                className="uc-chip flex items-center gap-1.5 hover:border-sky-500/50 hover:bg-sky-500/10 transition-colors text-sky-300 disabled:opacity-50"
              >
                {evolving ? <Loader2 size={10} className="animate-spin"/> : <InfinityIcon size={10}/>}
                {evo?.cycle ? `Optimizer · Cycle ${evo.cycle} · ${evo.trigger_armed ? "TRIGGER ARMED" : "watching pulse"}` : "Optimizer · trigger-based"}
              </button>
              <span className="uc-chip flex items-center gap-1.5" data-testid="oracle-networth-chip">
                <Landmark size={10} className="text-emerald-400"/>
                Net Worth ${Math.round(stats.net_worth).toLocaleString()}
              </span>
              <span className="uc-chip" data-testid="oracle-ventures-chip">{stats.ventures} Ventures Synced</span>
              <span className="uc-chip flex items-center gap-1.5 text-amber-400">
                <Eye size={10}/> Live Read Access
              </span>
            </div>
          )}
        </header>

        <MorningBriefing />
        {blockedCount > 0 && (
          <a href="/" className="mb-4 flex items-center gap-3 border border-rose-500/50 bg-rose-500/[0.08] px-4 py-2.5"
             data-testid="critical-mandate-alert">
            <span className="uc-dot bg-rose-400 uc-pulsing"></span>
            <span className="font-mono-uc text-[10px] uppercase tracking-widest text-rose-300">
              Critical Mandate Alert · {blockedCount} hard-blocked — Sovereign Override required on the Operations Console
            </span>
          </a>
        )}
        <GenesisLedger refreshKey={ledgerKey} />

        <div ref={bodyRef} className="flex-1 overflow-y-auto uc-scroll space-y-4 py-2 pr-2" data-testid="oracle-messages">
          {messages.length === 0 && !sending && (
            <div className="border border-amber-500/20 bg-amber-500/[0.03] p-5 max-w-2xl" data-testid="oracle-welcome">
              <div className="font-mono-uc text-[9px] uppercase tracking-widest text-amber-400 mb-2">The Chairman is present</div>
              <p className="text-white/70 text-sm leading-relaxed">
                Commander. I hold the boardroom — Scout, Sentinel, Strategist, Auditor, Creator,
                Executor, Controller — and a live read of your Net Worth Pulse and Venture Cockpit.
                Issue your supreme command.
              </p>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === "commander" ? "justify-end" : "justify-start"}`}>
              <div
                data-testid={`oracle-msg-${m.role}`}
                className={`max-w-[80%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "commander"
                    ? "border border-amber-500/40 bg-amber-500/[0.06] text-white/90"
                    : "border border-white/10 bg-black text-white/80"
                }`}
              >
                <div className={`font-mono-uc text-[8px] uppercase tracking-widest mb-1.5 ${m.role === "commander" ? "text-amber-400" : "text-white/40"}`}>
                  {m.role === "commander" ? "Commander" : "Sovereign Oracle"}
                </div>
                {m.content}
                {m.meta?.command && m.meta?.executed && (
                  <div className="mt-2">
                    <span className="font-mono-uc text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-emerald-500/40 text-emerald-300 inline-flex items-center gap-1" data-testid="oracle-command-chip">
                      <Zap size={8}/> Executed · {m.meta.command}
                    </span>
                  </div>
                )}
                {m.meta?.signoff_ref && (
                  <SignoffCard refData={m.meta.signoff_ref} />
                )}
                {m.meta?.rfq && (
                  <SovereignRFQCard rfq={m.meta.rfq} />
                )}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="border border-white/10 bg-black px-4 py-3 text-white/50 text-xs font-mono-uc flex items-center gap-2" data-testid="oracle-thinking">
                <Loader2 size={12} className="animate-spin text-amber-400"/> The Oracle is deliberating…
              </div>
            </div>
          )}
        </div>

        <div className="pt-4 border-t border-white/10 flex gap-2">
          <button
            data-testid="oracle-mic-btn"
            onClick={toggleMic}
            disabled={transcribing}
            title={recording ? "Stop recording" : "Dictate a supreme command (Whisper STT)"}
            className={`w-11 self-stretch flex-none border flex items-center justify-center transition-colors disabled:opacity-40 ${
              recording
                ? "border-rose-500/60 text-rose-400 bg-rose-500/10 animate-pulse"
                : "border-white/15 text-white/60 hover:border-amber-500/50 hover:text-amber-400"}`}
          >
            {transcribing ? <Loader2 size={14} className="animate-spin"/> : recording ? <Square size={13}/> : <Mic size={14}/>}
          </button>
          <textarea
            data-testid="oracle-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={2}
            placeholder={recording ? "Recording — speak your command…" : transcribing ? "Transcribing…" : "Issue a supreme command to the Chairman…"}
            className="flex-1 bg-black border border-white/15 focus:border-amber-500/50 outline-none p-3 text-sm text-white/90 resize-none"
          />
          <button
            data-testid="oracle-send-btn"
            onClick={send}
            disabled={sending || !input.trim()}
            className="uc-btn-amber px-5 flex items-center gap-2 font-mono-uc text-[10px] uppercase tracking-wider disabled:opacity-40"
          >
            {sending ? <Loader2 size={13} className="animate-spin"/> : <Send size={13}/>} Command
          </button>
        </div>
      </div>
      <GenesisModal open={genesisOpen} onClose={() => setGenesisOpen(false)} onLaunched={onGenesisLaunched} />
    </div>
  );
}
